import { createContext, PropsWithChildren, useCallback, useContext, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ClientCore } from 'client-core';

/**
 * The SharedClientCoreSubscriptionContext creates a single subscription per ClientCore that can be
 * shared between multiple components. Components can use this subscription instead of subscribing
 * themselves to ensure that they all share the same version of the ClientCore's internal state.
 */

type UnsubscribeFn = () => void;
type SubscribeFn = (clientCore: ClientCore, onStoreChange: () => void) => UnsubscribeFn;

interface SharedClientCoreSubscriptionContextValue {
  subscribe: SubscribeFn;
}

const SharedClientCoreSubscriptionContext = createContext<SharedClientCoreSubscriptionContextValue>(
  {
    subscribe: () => {
      throw Error('missing SharedClientCoreSubscriptionContext');
    },
  },
);

interface ClientCoreSubscription {
  subscribers: (() => void)[];
  clientCoreListener: () => void;
}

export function SharedClientCoreSubscriptionContextProvider({ children }: PropsWithChildren<{}>) {
  const subscriptions = useRef(new Map<ClientCore, ClientCoreSubscription>());

  const ensureSubscription = useCallback((clientCore: ClientCore) => {
    const existingSubscription = subscriptions.current.get(clientCore);
    if (existingSubscription) {
      return existingSubscription;
    }
    const newSubscription: ClientCoreSubscription = {
      subscribers: [],
      clientCoreListener: () => {
        // Ensures that all subscribers get called in one render pass. Normally, re-renders might
        // take place after each subscriber is called, but no render will take place during a call
        // to unstable_batchedUpdates. Instead, all components slated for re-render due to their
        // subscriptions will re-render in one go.
        unstable_batchedUpdates(() => {
          newSubscription.subscribers.forEach((s) => s());
        });
      },
    };

    // This is quite broad, since we need to capture any change to the ClientCore. Subscribers will
    // filter for stuff they're interested in based on changes of the referential identity of the
    // parts of the ClientCore that they actually used using useSyncExternalStore.
    clientCore.onAny(newSubscription.clientCoreListener);

    subscriptions.current.set(clientCore, newSubscription);
    return newSubscription;
  }, []);

  const cleanUpSubscriptionIfNecessary = useCallback((clientCore: ClientCore) => {
    const existingSubscription = subscriptions.current.get(clientCore);
    if (!existingSubscription || existingSubscription.subscribers.length) {
      return;
    }
    clientCore.offAny(existingSubscription.clientCoreListener);
    subscriptions.current.delete(clientCore);
  }, []);

  const subscribe = useCallback(
    (clientCore: ClientCore, onStoreChange: () => void) => {
      const subscription = ensureSubscription(clientCore);
      subscription.subscribers.push(onStoreChange);
      return () => {
        subscription.subscribers.splice(subscription.subscribers.indexOf(onStoreChange), 1);
        cleanUpSubscriptionIfNecessary(clientCore);
      };
    },
    [cleanUpSubscriptionIfNecessary, ensureSubscription],
  );

  return (
    <SharedClientCoreSubscriptionContext.Provider value={{ subscribe }}>
      {children}
    </SharedClientCoreSubscriptionContext.Provider>
  );
}

export function useSharedClientCoreSubscriptionContext() {
  return useContext(SharedClientCoreSubscriptionContext);
}
